<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AssistDataChip from './AssistDataChip.vue';
import ChipPopover from 'dashboard/components-next/Conversation/ContextChips/ChipPopover.vue';
import { toggleExclusiveOpen } from 'dashboard/components-next/Conversation/ContextChips/helpers/exclusiveOpen';

// SiteUp (Cartões de Apoio): a linha de chips que acompanha a sugestão da IA.
// Variação A (Von Restorff): UM chip dominante por contexto (o `primary` do
// backend) e os demais discretos. Se tudo chama atenção, nada chama atenção.
const props = defineProps({
  tips: {
    type: Array,
    default: () => [],
  },
  cards: {
    type: Array,
    default: () => [],
  },
  // Conversa atual: o painel do chip recarrega os dados por ela quando a busca falhou.
  conversationId: {
    type: [Number, String],
    default: null,
  },
  // O chip de imóveis abre um modal que insere texto NO EDITOR DE RESPOSTA.
  // Superfície sem esse editor montado (o painel do copilot ocupa o rodapé no
  // lugar dele) não oferece a ação, senão o clique promete algo que não acontece.
  allowPropertyModal: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['propertyModal']);

const { t } = useI18n();

// Acima de 6 a linha vira uma barra de ferramentas e a hierarquia se perde.
const MAX_CHIPS = 6;

const KIND_ICONS = {
  quick_link: 'i-ri-external-link-line',
  property_list: 'i-ri-home-4-line',
  orders: 'i-ri-shopping-bag-3-line',
  access: 'i-ri-key-2-line',
  disc_profile: 'i-ri-user-voice-line',
};

// min-w-0 no chip E no rótulo: item flex não encolhe abaixo do próprio conteúdo
// (min-width auto), então sem os dois o `truncate` fica decorativo e o rótulo
// longo estoura o container em vez de virar reticências.
const CHIP_BASE =
  'inline-flex min-h-8 min-w-0 max-w-full items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand';
const CHIP_PRIMARY = 'bg-n-brand text-white hover:opacity-90';
const CHIP_GHOST =
  'border border-transparent text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12';
const CHIP_PASSIVE = 'border border-transparent text-n-slate-11 cursor-default';

// Link só vira chip com esquema conhecido: caminho relativo resolve contra a
// origem do dashboard e passa, javascript:/data: cai fora.
const SAFE_PROTOCOLS = ['http:', 'https:'];

// Os dois únicos inventários que o PropertySuggestionModal conhece, a mesma
// lista que o ReplyBox usa para barrar a abertura. Repetida aqui porque a
// validação lá só protege o modal: sem ela no chip, origem desconhecida vira
// botão clicável que promete abrir algo e morre calado.
const PROPERTY_SOURCES = ['lia', 'todas'];

const asArray = value => (Array.isArray(value) ? value : []);

const labelOf = item =>
  typeof item?.label === 'string' ? item.label.trim() : '';

const safeHref = href => {
  if (typeof href !== 'string' || !href.trim()) return null;
  try {
    const { protocol } = new URL(href, window.location.origin);
    return SAFE_PROTOCOLS.includes(protocol) ? href : null;
  } catch {
    return null;
  }
};

const textList = value =>
  asArray(value).filter(item => typeof item === 'string' && item.trim());

// Confiança vem como fração (0.82). Formatada aqui, e não no template, pra não
// deixar sinal solto de string crua no HTML.
const confidenceLabel = confidence =>
  typeof confidence === 'number' && Number.isFinite(confidence)
    ? `${t('CONVERSATION.ASSIST_CHIPS.CONFIDENCE')} ${Math.round(confidence * 100)}%`
    : '';

// Contato analisado antes da fatia (ou perfil truncado) chega sem contagem: a
// linha mostra só a confiança, em vez de anunciar "0 mensagens".
const analyzedLabel = count =>
  Number.isInteger(count) && count > 0
    ? t('CONVERSATION.ASSIST_CHIPS.ANALYZED_MESSAGES', { count })
    : '';

// Confiança e volume dizem a mesma coisa (o quanto a hipótese vale), então vão
// na mesma linha, separadas por ponto médio.
const contextLine = data =>
  [confidenceLabel(data?.confidence), analyzedLabel(data?.analyzed_count)]
    .filter(Boolean)
    .join(' · ');

const DISC_LETTERS = ['D', 'I', 'S', 'C'];

// O tipo é a letra dominante ("I"). Quando o perfil vier com um segundo traço
// ("ID"), ele entra como coadjuvante; letra que não existe no dicionário cai
// fora e o painel volta ao rótulo do cartão.
const discLetters = type =>
  (typeof type === 'string' ? type.toUpperCase().split('') : []).filter(
    letter => DISC_LETTERS.includes(letter)
  );

const typeName = letter =>
  t(`CONVERSATION.ASSIST_CHIPS.PROFILE_TYPES.${letter}`);

// Um popover aberto por vez, pelo mesmo helper dos chips do header: dois painéis
// flutuando ao mesmo tempo se sobrepõem em cima da barra de ações.
const openInfoKey = ref(null);

const toggleInfo = key => {
  openInfoKey.value = toggleExclusiveOpen(openInfoKey.value, key);
};

const closeInfo = () => {
  openInfoKey.value = null;
};

// Card malformado (sem rótulo, com ação que não sabemos honrar) é descartado
// AQUI, e não no template: assim o orçamento MAX_CHIPS e o `hasChips` contam
// exatamente o que vai pra tela, em vez de o card sumir depois da conta.
const resolveCard = (card, index) => {
  const label = labelOf(card);
  if (!label) return null;

  const type = card.action?.type;
  const items = Array.isArray(card.items) ? card.items : [];
  const chip = {
    key: card.id ?? `card-${index}`,
    id: card.id,
    kind: card.kind,
    label,
    description: card.description,
    items,
    icon: KIND_ICONS[card.kind] || 'i-ri-sparkling-line',
    class: `${CHIP_BASE} ${card.primary ? CHIP_PRIMARY : CHIP_GHOST}`,
  };

  // Imóveis: com dados o chip abre o painel com as opções; sem dados ele segue abrindo o
  // modal, que busca na hora. Sem editor de resposta na tela, chip de imóvel sem dado não
  // tem nada a oferecer e cai fora — com dado, o painel sozinho já vale.
  if (type === 'property_modal') {
    const { source } = card.action;
    if (!PROPERTY_SOURCES.includes(source)) return null;
    if (items.length) return { ...chip, type: 'data', source };
    return props.allowPropertyModal ? { ...chip, type, source } : null;
  }

  if (type === 'url') {
    const href = safeHref(card.action.href);
    return href ? { ...chip, type, href } : null;
  }

  // Cartão de leitura (perfil de interação): não tem ação nenhuma, o clique só
  // abre as dicas. Sem dica não há o que abrir, e aí ele segue como cartão de dados.
  const tips = textList(card.data?.tips);
  if (!card.action && tips.length) {
    const [dominant, secondary] = discLetters(card.data?.type);
    return {
      ...chip,
      type: 'info',
      tips,
      footer: typeof card.footer === 'string' ? card.footer : '',
      letter: dominant ?? '',
      title: dominant ? typeName(dominant) : label,
      secondary: secondary
        ? t('CONVERSATION.ASSIST_CHIPS.PROFILE_SECONDARY', {
            type: typeName(secondary),
          })
        : '',
      context: contextLine(card.data),
    };
  }

  // Pedidos e acessos só existem com dado dentro: o cartão É a lista.
  return items.length ? { ...chip, type: 'data' } : null;
};

const visibleCards = computed(() =>
  asArray(props.cards).map(resolveCard).filter(Boolean).slice(0, MAX_CHIPS)
);

const visibleTips = computed(() =>
  asArray(props.tips)
    .map((tip, index) => ({
      key: `tip-${index}`,
      label: labelOf(tip),
      description: tip?.description,
    }))
    .filter(tip => tip.label)
    .slice(0, MAX_CHIPS - visibleCards.value.length)
);

// Sugestão nova traz outro envelope: o painel aberto do cartão anterior não pode
// sobreviver à troca falando do cliente errado.
watch(
  () => props.cards,
  () => {
    closeInfo();
  }
);

// Sem chip o componente sai do DOM (v-if, não v-show): o slot `tips` do
// CopilotEditor mora dentro de um `space-y-2`, que o Tailwind compila como
// `> :not([hidden]) ~ :not([hidden])`. Um nó escondido por `display:none` não
// ganha o atributo `hidden`, então continuaria contando como irmão e deixaria
// um espaço morto no painel do copilot de quem não tem cartão nenhum.
const hasChips = computed(
  () => visibleCards.value.length > 0 || visibleTips.value.length > 0
);
</script>

<template>
  <div
    v-if="hasChips"
    role="group"
    :aria-label="t('CONVERSATION.ASSIST_CHIPS.ARIA_LABEL')"
    class="flex min-w-0 flex-wrap items-center gap-1.5"
  >
    <template v-for="card in visibleCards" :key="card.key">
      <AssistDataChip
        v-if="card.type === 'data'"
        :card="card"
        :conversation-id="conversationId"
        :allow-property-modal="allowPropertyModal"
        @property-modal="emit('propertyModal', $event)"
      />
      <a
        v-else-if="card.type === 'url'"
        v-tooltip.top="card.description"
        :href="card.href"
        target="_blank"
        rel="noopener noreferrer"
        :class="card.class"
      >
        <span class="size-3.5 shrink-0" :class="card.icon" aria-hidden="true" />
        <span class="min-w-0 truncate">{{ card.label }}</span>
      </a>

      <!-- Popover flutuante (o mesmo ChipPopover dos chips do header): a barra
           mora na bolha da sugestão e no painel do copilot, os dois com rolagem
           própria. Teleportado pro body, o painel não é recortado por nenhum dos
           dois, acompanha o gatilho na rolagem e se fecha quando ele sai da tela. -->
      <ChipPopover
        v-else-if="card.type === 'info'"
        :open="openInfoKey === card.key"
        @close="closeInfo"
      >
        <template #trigger>
          <button
            v-tooltip.top="card.description"
            type="button"
            data-testid="assist-chip-info"
            :aria-expanded="openInfoKey === card.key"
            :aria-label="`${card.label}: ${t('CONVERSATION.ASSIST_CHIPS.PROFILE_DETAILS')}`"
            :class="card.class"
            @click.stop="toggleInfo(card.key)"
          >
            <span
              class="size-3.5 shrink-0"
              :class="card.icon"
              aria-hidden="true"
            />
            <span class="min-w-0 truncate">{{ card.label }}</span>
            <span
              class="i-ri-arrow-down-s-line size-3.5 shrink-0 transition-transform"
              :class="{ 'rotate-180': openInfoKey === card.key }"
              aria-hidden="true"
            />
          </button>
        </template>

        <div
          role="region"
          data-testid="assist-chip-info-panel"
          :aria-label="t('CONVERSATION.ASSIST_CHIPS.PROFILE_PANEL')"
          class="min-w-0 text-xs text-n-slate-11"
        >
          <!-- Badge com a letra dominante e o tipo por extenso: o rótulo do chip
               já diz o estilo, aqui o painel abre com a leitura do mockup. -->
          <div class="flex min-w-0 items-start gap-2">
            <span
              v-if="card.letter"
              data-testid="assist-chip-info-letter"
              class="grid size-7 shrink-0 place-items-center rounded-lg bg-n-brand text-sm font-semibold text-white"
              aria-hidden="true"
            >
              {{ card.letter }}
            </span>
            <div class="min-w-0">
              <p class="m-0 text-sm font-semibold text-n-slate-12">
                {{ card.title }}
                <span
                  v-if="card.secondary"
                  class="text-xs font-normal text-n-slate-10"
                >
                  {{ card.secondary }}
                </span>
              </p>
              <p v-if="card.context" class="m-0 mt-0.5 text-n-slate-10">
                {{ card.context }}
              </p>
            </div>
          </div>
          <ul class="m-0 mt-2 flex list-none flex-col gap-1 p-0">
            <li
              v-for="tip in card.tips"
              :key="tip"
              class="flex min-w-0 items-start gap-1.5"
            >
              <span
                class="i-ri-check-line mt-0.5 size-3.5 shrink-0 text-n-teal-10"
                aria-hidden="true"
              />
              <span class="min-w-0 break-words">{{ tip }}</span>
            </li>
          </ul>
          <p v-if="card.footer" class="m-0 mt-2 text-n-slate-10">
            {{ card.footer }}
          </p>
        </div>
      </ChipPopover>

      <button
        v-else
        v-tooltip.top="card.description"
        type="button"
        :class="card.class"
        @click="emit('propertyModal', card.source)"
      >
        <span class="size-3.5 shrink-0" :class="card.icon" aria-hidden="true" />
        <span class="min-w-0 truncate">{{ card.label }}</span>
      </button>
    </template>

    <span
      v-for="tip in visibleTips"
      :key="tip.key"
      v-tooltip.top="tip.description"
      :class="`${CHIP_BASE} ${CHIP_PASSIVE}`"
    >
      <span
        class="i-ri-lightbulb-line size-3.5 shrink-0 text-n-amber-10"
        aria-hidden="true"
      />
      <span class="min-w-0 truncate">{{ tip.label }}</span>
    </span>
  </div>
</template>
